import React, {useCallback, useState} from 'react';

import isArchived from 'archive/isArchived';
import {
  getAllBoxSchemasFromCache,
  getAllContainerSchemasFromCache,
  getAllLocationSchemasFromCache,
  getAllPlateSchemasFromCache,
  getAllSchemaInterfacesExtendingSchemaFromCache,
  getAllTagSchemasFromCache,
  getStorableSchemaFromCache,
  getTagSchemaOrSchemaInterfaceFromCache,
} from 'lookup-config/AllSchemasQueries';
import {
  LookupStepInfo,
  LookupStepType,
  NotebookColumnItemType,
  NotebookColumnTypecheckLookupStepSpec,
  ALL_STORABLE_NOTEBOOK_COLUMN_ITEM_TYPES,
} from 'lookup-config/LookupConfigModalConstants';
import LookupConfigModalErrorIndicator from 'lookup-config/LookupConfigModalErrorIndicator';
import {getLookupStepSchemaId} from 'lookup-config/LookupConfigUtils';
import {getLookupStepInfoForNotebookTypeCheckItem} from 'lookup-config/LookupStepInitializerUtils';
import {LookupStepSectionHeader} from 'lookup-config/LookupStepSectionHeader';
import {NotebookColumnInfo} from 'notebook/structured-tables/lookup-columns/runLookupConfigModalForNotebook';
import {Schema} from 'schemas/SchemaConstants';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {LabelStyle} from 'ui-library/inputs/InputConstants';
import Menu from 'ui-library/menus/Menu';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import APIUtil from 'util/APIUtil';

import {
  getAnySchemaOptionName,
  getColumnInfoByProvenanceUuid,
  getDefaultLookupStepForInputColumn,
} from './NotebookLookupColumnUtils';

// exported for test only
export const DATA_TEST_ATTRS = {
  columnSelector: {component: 'NotebookFirstLookupStepForm', element: 'columnSelector'},
  entitySchemaSelector: {component: 'NotebookFirstLookupStepForm', element: 'entitySchemaSelector'},
};

export interface NotebookFirstLookupStepFormProps {
  disabled: boolean;
  notebookColumnInfos: Array<NotebookColumnInfo>;
  notebookInputColumnProvenanceUuid: string | null;
  setNotebookInputColumnProvenanceUuid: (provenanceUuid: string | null) => void;
  lookupStepGroup: Array<LookupStepInfo>;
  // Caution: this callback is shared by SchemaFieldSelectTree. The SchemaFieldSelectTree itself is well-behaved,
  // and it will not blow anything away, but this component should be careful not to blow away lookupStepGroup[1]
  // unless the schema has actually changed and the SchemaFieldSelectTree actually needs to reset.
  updateLookupStepGroup: (lookupStepGroup: Array<LookupStepInfo>) => void;
}

/**
 * This Form component allows users to select a source column for a lookup column, along with the desired
 * schema of the items in that column when performing the lookup.
 *
 * This component works by taking user input and updating the `lookupStepGroups` stored on the parent
 * using the prop `updateLookupStepGroup`.
 */
export default function NotebookFirstLookupStepForm(props: NotebookFirstLookupStepFormProps): JSX.Element {
  const [isCollapsed, setIsCollapsed] = useState(props.disabled);
  const selectedInputColumnInfo = getColumnInfoByProvenanceUuid(
    props.notebookColumnInfos,
    props.notebookInputColumnProvenanceUuid
  );

  // `props.disabled` should only change when switching between this and a subsequent lookup step group, so
  // memoizing this callback should help a bit with subsequent renders of when the user is interacting with
  // this specific form.
  const _isModalDisabled = useCallback(
    () => props.disabled || props.notebookColumnInfos.length === 0,
    [props.disabled, props.notebookColumnInfos]
  );

  // Helpers to update lookup configuration
  function updateSelectedColumn(provenanceUuid: string): void {
    // The input column provenance UUID is not stored as part of the lookup config, but instead persisted on
    // the lookup column itself, so the lookup step group and input column provenance UUID must be updated
    // separately.
    props.setNotebookInputColumnProvenanceUuid(provenanceUuid);
    const {lookupStep: newLookupStep, additionalStepMetadata} = getDefaultLookupStepForInputColumn(
      props.notebookColumnInfos,
      provenanceUuid
    );
    if (newLookupStep) {
      props.updateLookupStepGroup([
        getLookupStepInfoForNotebookTypeCheckItem(
          newLookupStep as NotebookColumnTypecheckLookupStepSpec,
          null,
          additionalStepMetadata
        ),
      ]);
    }
  }

  function updateSelectedSchema(newSelectedSchemaId: string | null): void {
    const newFirstLookupStep: NotebookColumnTypecheckLookupStepSpec = {
      type: LookupStepType.NOTEBOOK_COLUMN_TYPECHECK,
      notebookColumnItemType: selectedInputColumnInfo!.itemType,
    };
    if (newSelectedSchemaId != null) {
      const notebookColumnSchemaKey = APIUtil.isStorableSchema(newSelectedSchemaId)
        ? 'notebookColumnStorableSchema'
        : 'notebookColumnEntitySchema';
      newFirstLookupStep[notebookColumnSchemaKey] = newSelectedSchemaId;
    }
    const newFirstLookupStepInfo = getLookupStepInfoForNotebookTypeCheckItem(newFirstLookupStep, null, {
      storableLookupMustBeParent: selectedInputColumnInfo!.storableLookupMustBeParent,
    });
    props.updateLookupStepGroup([newFirstLookupStepInfo]);
  }

  const selectedColumnIsStorableLookup =
    selectedInputColumnInfo?.itemType != null &&
    ALL_STORABLE_NOTEBOOK_COLUMN_ITEM_TYPES.includes(selectedInputColumnInfo.itemType);

  function getStorableAllowedSchemas(
    itemType?: NotebookColumnItemType,
    isParentStorableOnly?: boolean
  ): Array<Schema> {
    if (itemType === NotebookColumnItemType.CONTAINER) {
      return getAllContainerSchemasFromCache();
    } else if (itemType === NotebookColumnItemType.PLATE) {
      return getAllPlateSchemasFromCache();
    } else if (itemType === NotebookColumnItemType.BOX) {
      return getAllBoxSchemasFromCache();
    } else if (itemType === NotebookColumnItemType.LOCATION) {
      return getAllLocationSchemasFromCache();
    } else {
      const parentStorableSchemas = [
        ...getAllLocationSchemasFromCache(),
        ...getAllPlateSchemasFromCache(),
        ...getAllBoxSchemasFromCache(),
      ];
      if (isParentStorableOnly) {
        return parentStorableSchemas;
      }
      return [...getAllContainerSchemasFromCache(), ...parentStorableSchemas];
    }
  }

  let allowedSchemas: Array<Schema> = [];
  let selectedSchemaId: string | null = null;
  let selectedSchema: Schema | null = null;
  if (props.lookupStepGroup.length > 0) {
    // Identify the currently selected schema using the current lookup step group.
    selectedSchemaId = getLookupStepSchemaId(props.lookupStepGroup[0]);

    // Identify allowed schemas for selected column type (to allow the user to change schemas).
    if (props.notebookColumnInfos.length > 0) {
      if (selectedColumnIsStorableLookup) {
        allowedSchemas = getStorableAllowedSchemas(
          selectedInputColumnInfo?.itemType,
          selectedInputColumnInfo?.storableLookupMustBeParent
        );
        selectedSchema = selectedSchemaId ? getStorableSchemaFromCache(selectedSchemaId) : null;
      } else {
        allowedSchemas = [
          ...getAllTagSchemasFromCache(),
          ...getAllSchemaInterfacesExtendingSchemaFromCache(),
        ];
        selectedSchema = selectedSchemaId ? getTagSchemaOrSchemaInterfaceFromCache(selectedSchemaId) : null;
      }

      if (selectedInputColumnInfo?.schemaId != null) {
        allowedSchemas = allowedSchemas.filter(
          (schema) => schema && schema.id === selectedInputColumnInfo.schemaId
        );
      }
    }
  }

  return (
    <>
      <LookupStepSectionHeader
        isCollapsed={isCollapsed}
        setIsCollapsed={setIsCollapsed}
        isActive={!props.disabled}
        lookupStepGroup={props.lookupStepGroup}
        parentSchema={null}
        error={null}
      />
      {!isCollapsed && (
        <Row>
          <Col xs={6}>
            <FieldLabel label="Column" labelStyle={LabelStyle.LABEL} />
            <SelectDropdown
              dataTestAttrs={DATA_TEST_ATTRS.columnSelector}
              disabled={_isModalDisabled()}
              menu={
                <SearchableMenu
                  height={Menu.HEIGHTS.MEDIUM}
                  width={Menu.WIDTHS.FULL}
                  selectedValue={props.notebookInputColumnProvenanceUuid}
                  optionDatas={props.notebookColumnInfos.map(({name, provenanceUuid}) => ({
                    value: provenanceUuid,
                    name,
                  }))}
                  onSelect={(provenanceUuid) => {
                    updateSelectedColumn(provenanceUuid);
                  }}
                />
              }
              displayName={selectedInputColumnInfo?.name ?? 'Select column'}
            />
          </Col>
          <Col xs={6}>
            <FieldLabel
              label={selectedColumnIsStorableLookup ? 'Inventory in Column' : 'Entity in Column'}
              labelStyle={LabelStyle.LABEL}
            />
            <SelectDropdown
              dataTestAttrs={DATA_TEST_ATTRS.entitySchemaSelector}
              disabled={_isModalDisabled()}
              menu={
                <SearchableMenu
                  height={Menu.HEIGHTS.MEDIUM}
                  width={Menu.WIDTHS.FULL}
                  selectedValue={selectedSchemaId}
                  optionDatas={
                    allowedSchemas.length === 0
                      ? []
                      : [
                          // "Any Entity" or "Any <storable type>" depending on source column schema
                          {
                            value: null,
                            name: getAnySchemaOptionName(selectedInputColumnInfo?.itemType),
                          },
                          // Specific schemas
                          {
                            name: selectedColumnIsStorableLookup ? 'Inventory of type' : 'Entity of type',
                            isHeader: true,
                            className: 'schema-section',
                          },
                          ...allowedSchemas
                            .filter((schema) => schema != null && !isArchived(schema))
                            .map((schema) => ({
                              value: schema.id,
                              name: schema.name,
                            })),
                        ]
                  }
                  onSelect={(schemaId) => {
                    updateSelectedSchema(schemaId);
                  }}
                />
              }
              displayName={selectedSchema?.name ?? getAnySchemaOptionName(selectedInputColumnInfo?.itemType)}
            />
          </Col>
        </Row>
      )}
      {/* Show error if modal is disabled due to a lack of valid inputs. */}
      {props.notebookColumnInfos.length === 0 && (
        <LookupConfigModalErrorIndicator error="No valid source columns present in table" />
      )}
    </>
  );
}
